import * as React from 'react';
import {
    Button,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid, Radio,
    RadioGroup,
    TextField
} from '@material-ui/core';
import {API} from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import {Alert} from "@material-ui/lab";

const styles = {
    fullWidthGrid: {
        width: '100%',
        maxWidth: '100%',
    } as React.CSSProperties,
    jsonResponseContainer: {
        border: '3px solid lightgray',
        padding: '5px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
        overflowWrap: 'break-word',
        overflowX: 'auto',
        maxWidth: '100%',
    } as React.CSSProperties,
    prescriptionBlock: {
        backgroundColor: '#f5f5f5',
        border: '1px solid lightgray',
        borderRadius: '4px',
        marginBottom: '10px',
        overflowX: 'auto',
        padding: '10px',
    } as React.CSSProperties,
    formGroup: { width: 400 } as React.CSSProperties,
    radioGroup: { width: 225 } as React.CSSProperties,
    textField: { width: 400 } as React.CSSProperties,
};

const getErrorMessage = (error: any): string =>
    error?.response?.data?.message || error?.message || 'Unknown error';

const copyButtonStyle = (copied: boolean): React.CSSProperties => ({
    backgroundColor: copied ? '#4CAF50' : '#ffffff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    color: copied ? '#ffffff' : '#000000',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '10px',
    padding: '8px 16px',
});

interface IProps {
    data?: any
}

interface IState {
    copiedAll: boolean
    drugIds: string
    errorMessage: string
    id: string
    idType: string
    isLoading: boolean
    parsedPrescriptions: any[]
    response: any
}

class ListPrescriptionsSection extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            copiedAll: false,
            drugIds: '',
            errorMessage: '',
            id: '',
            idType: 'person',
            isLoading: false,
            parsedPrescriptions: [],
            response: ''
        };

        this.setIdValue = this.setIdValue.bind(this);
        this.handleIdTypeValue = this.handleIdTypeValue.bind(this);
        this.setDrugIds = this.setDrugIds.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCopyAll = this.handleCopyAll.bind(this);
    }

    public render() {
        return (
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
                spacing={3}
            >
                <Grid item xs>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Id Type</FormLabel>
                        <FormGroup style={styles.formGroup}>
                            <RadioGroup aria-label="idType"
                                        name="idType"
                                        value={this.state.idType}
                                        onChange={this.handleIdTypeValue}
                                        style={styles.radioGroup}>
                                <FormControlLabel value="person" control={
                                    <Radio/>
                                } label="PersonId"/>
                                <FormControlLabel value="profile" control={
                                    <Radio/>
                                } label="ProfileId"/>
                            </RadioGroup>
                        </FormGroup>
                    </FormControl>
                </Grid>

                <Grid item xs>
                    <TextField
                        style={styles.textField}
                        label="Id"
                        variant="outlined"
                        value={ this.state.id }
                        onChange={ this.setIdValue }
                    />
                </Grid>

                {this.state.idType === 'profile' &&
                    <Grid item xs>
                        <TextField
                            style={styles.textField}
                            label="Drug Ids <comma separated> (optional)"
                            variant="outlined"
                            value={ this.state.drugIds }
                            onChange={ this.setDrugIds }
                            multiline
                            rows={ 3 }
                        />
                    </Grid>
                }

                <Grid item xs>
                    <Button variant="outlined" color="default" onClick={this.handleSubmit}>
                        List Prescription
                    </Button>
                </Grid>

                { this.state.isLoading && <LoadingSpinner/> }

                { this.state.errorMessage &&
                    <Alert variant="filled" severity="info">
                        Error encountered: { this.state.errorMessage }
                    </Alert>
                }

                { this.state.response && (this.state.idType === 'person' || this.state.parsedPrescriptions.length === 0) &&
                    <Grid item xs={12} >
                        <h4>Prescription Details</h4>
                        <div style={styles.jsonResponseContainer}>
                            <JsonRenderComponent jsonObject={ this.state.response } printHtml={ false }/>
                        </div>
                    </Grid>
                }

                { this.state.parsedPrescriptions.length > 0 &&
                    <Grid item xs={12} style={styles.fullWidthGrid}>
                        <h4>Prescription Details ({this.state.parsedPrescriptions.length} results)</h4>
                        <button
                            onClick={this.handleCopyAll}
                            style={copyButtonStyle(this.state.copiedAll)}
                        >
                            {this.state.copiedAll ? '✓ Copied All Content!' : 'Copy All Content'}
                        </button>
                        {this.state.parsedPrescriptions.map((prescription, index) => (
                            <pre key={index} style={styles.prescriptionBlock}>
                                {JSON.stringify(prescription, null, 2)}
                            </pre>
                        ))}
                    </Grid>
                }
            </Grid>
        );
    }

    private handleCopyAll() {
        navigator.clipboard.writeText(
            JSON.stringify(this.state.parsedPrescriptions, null, 2)
        ).then(() => {
            this.setState({ copiedAll: true });
            setTimeout(() => this.setState({ copiedAll: false }), 2000);
        }).catch((err) => {
            console.error('Failed to copy:', err);
        });
    }

    private setIdValue(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({ id: event.target.value })
    }

    private handleIdTypeValue(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ idType: event.target.value, response: '', parsedPrescriptions: [], errorMessage: '' })
    }

    private setDrugIds(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({ drugIds: event.target.value })
    }

    private handleSubmit() {
        this.setState({ isLoading: true, errorMessage: '', response: '', parsedPrescriptions: [] });

        if (this.state.idType === 'person') {
            this.searchByPersonId();
        } else {
            this.searchByProfileId();
        }

    }

    private searchByPersonId() {
        const apiName = 'ApaqPortalAPI';
        const path = '/order/prescription/get-prescriptions?personId=' + this.state.id;

        API.get(apiName, path, {}).then((listPrescriptionResponse: any) => {
            console.log(listPrescriptionResponse);

            this.setState({ response: listPrescriptionResponse, isLoading: false });
        }).catch((error) => {
            console.log(error);

            this.setState({ errorMessage: getErrorMessage(error), isLoading: false });
        });
    }

    private searchByProfileId() {
        const apiName = 'ApaqPortalAPI';
        const path = '/prescription/profile/search';
        const drugIdList = this.state.drugIds.split(',');

        const config = {
            body: {
                drugIds: this.validateDrugIdList(drugIdList) ? drugIdList : [],
                profileId: this.state.id
            },
            headers: {}
        }

        console.log(config);

        API.post(apiName, path, config).then((listPrescriptionResponse: any) => {
            console.log(listPrescriptionResponse);

            const prescriptions = Array.isArray(listPrescriptionResponse.prescriptions)
                ? listPrescriptionResponse.prescriptions.map((item: any) => {
                    if (typeof item === 'string') {
                        try { return JSON.parse(item); } catch (e) { return item; }
                    }
                    return item;
                })
                : [];

            this.setState({ response: listPrescriptionResponse, parsedPrescriptions: prescriptions, isLoading: false });
        }).catch((error) => {
            console.log(error);

            this.setState({ errorMessage: getErrorMessage(error), isLoading: false });
        });
    }

    private validateDrugIdList(drugIdList: string[]): boolean {
        let isValidList = true;
        drugIdList.forEach((id) => {
            if (id.length > 0 && !id.match("^[A-Za-z0-9]{24}$")) {
                alert(`Invalid drugId ${id}, ignoring drugIds input`);
                isValidList = false;
            }
        });

        return isValidList;
    }
}

export default ListPrescriptionsSection